<script setup lang="ts">
/**
 * Mark a withdraw request as settled — operator enters the bank's
 * outbound transfer reference (UTR / SWIFT-MT103 / etc.) and confirms.
 */
import { computed, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Input from '@uiKit/elements/Input.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'

interface Props {
  visible: boolean
  loading?: boolean
  apiError?: string | null
}

const MIN_REFERENCE_LENGTH = 3

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', value: { settlementReference: string }): void
}>()

const settlementReference = ref('')
const localError = ref('')

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      settlementReference.value = ''
      localError.value = ''
    }
  },
)

const trimmedReference = computed(() => settlementReference.value.trim())

function handleConfirm() {
  if (trimmedReference.value.length < MIN_REFERENCE_LENGTH) {
    localError.value = `Reference must be at least ${MIN_REFERENCE_LENGTH} characters.`
    return
  }
  localError.value = ''
  emit('confirm', { settlementReference: trimmedReference.value })
}

function handleInput(value: string) {
  settlementReference.value = value
  if (localError.value) localError.value = ''
}

function handleClose() {
  if (props.loading) return
  emit('update:visible', false)
}
</script>

<template>
  <Dialog
    :visible="visible"
    width="480px"
    max-height="70vh"
    :show-close-icon="!loading"
    :dismissable="!loading"
    @update:visible="(v) => !v && handleClose()"
  >
    <template #header>
      <Text type="page-title">Mark as settled</Text>
    </template>

    <Box flex direction="col" gap="3">
      <Text type="body-sm" color="muted">
        Record the bank's outbound transfer reference (UTR / SWIFT MT103 / wire confirmation). The
        investor wallet is debited and the ledger row is written when this action commits.
      </Text>

      <FormField label="Settlement reference" required :error="localError">
        <Input
          :model-value="settlementReference"
          placeholder="UTR-2026-…"
          :disabled="loading"
          @update:model-value="handleInput"
        />
      </FormField>

      <Message v-if="apiError" color="danger" variant="outlined" size="small" :message="apiError" />
    </Box>

    <template #footer>
      <Box flex justify="end" gap="2">
        <Button
          label="Cancel"
          variant="text"
          color="secondary"
          :disabled="loading"
          @click="handleClose"
        />
        <Button label="Mark settled" color="primary" :loading="loading" @click="handleConfirm" />
      </Box>
    </template>
  </Dialog>
</template>
